import { Component, EventEmitter, Output, Input } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { BaseClass } from '@zerops/fe/core';
import { Subject, combineLatest } from 'rxjs';
import {
  mergeMap,
  takeUntil,
  map,
  filter,
  distinctUntilChanged,
  switchMap,
  take
} from 'rxjs/operators';
import keys from 'lodash-es/keys';
import orderBy from 'lodash-es/orderBy';
import { State } from '@app/models';
import {
  authActiveUserServicesActiveGrouped,
  identity,
  authActiveClientUser,
  activeManagedServicesAliases,
  authorType,
  cloudDnsRoleCode,
  authSlice,
  TokenRefreshService
} from '@app/base/auth-base';
import { clientServicesListEntities } from '@app/base/client-services-base';
import { ServicesTypes, ServiceCategory } from '@app/base/services-base';
import { SetTicketTriggerState, TicketTriggerOpen } from '@app/common/tickets-trigger';
import { currencyMap } from '@app/common/settings';
import { environment } from 'environments/environment';
import { environment as environmentForDevel } from 'environments/environment.devel';
import { b64EncodeUnicode, toBoolean } from 'utils';
import { FormControl } from '@angular/forms';
import { LocalRedirectionService } from '@app/services';
import { localhostRedirectStorageKey } from '@app/constants';

@Component({
  selector: 'vshcz-services-card',
  templateUrl: './services-card.container.html',
  styleUrls: [ './services-card.container.scss' ]
})
export class ServicesCardContainer extends BaseClass {

  // # Event streams
  onContactSupportForNewService$ = new Subject<void>();

  // # Data
  // -- sync
  serviceTypeExternalMap = keys(ServicesTypes).reduce((obj, key) => {
    const item = ServicesTypes[key];
    obj[item.key] = item.type === ServiceCategory.External;
    return obj;
  }, {});
  enableCDN = toBoolean(environment.enableCDN);
  enableCloudMail = toBoolean(environment.enableCloudMail);
  isProductionEnv = environment.envName === 'prod';
  slideControl = new FormControl(this._getLocalhostRedirect());

  // -- async
  groupedServices$ = this._store.pipe(select(authActiveUserServicesActiveGrouped));
  activeManagedServices$ = this._store.pipe(
    select(activeManagedServicesAliases),
    map((s) => s.join(','))
  );
  graphsAndSettingsServices$ = this.groupedServices$.pipe(
    map((groupedServices) => groupedServices.filter(
      (group) => !this.serviceTypeExternalMap[group.key] && group.key !== 'CDN')
    )
  );
  latestClientServices$ = this._store.pipe(
    select(clientServicesListEntities),
    map((clientServices) => {

      if (clientServices && clientServices.length) {
        const filteredClientServices = clientServices.filter(
          (service) => service.clientZoneStatus !== 'BEING_ACTIVATED'
        );
        return orderBy(filteredClientServices, [ 'activationDate' ], [ 'desc' ]).slice(0, 5);
      }

      return [];
    })
  );
  currencyMap$ = this._store.pipe(
    select(currencyMap)
  );
  clientUser$ = this._store.pipe(
    select(authActiveClientUser),
    filter((user) => !!user)
  );
  authorType$ = this._store.pipe(select(authorType));
  cloudDnsRoleCode$ = this._store.pipe(select(cloudDnsRoleCode));
  showCloudDns$ = combineLatest([
    this.clientUser$,
    this.authorType$,
    this.cloudDnsRoleCode$
  ]).pipe(
    map(([ clientUser, author, code ]) => (clientUser.cloudDnsEnabled && author !== 'BACKOFFICE')
      || (clientUser.cloudDnsEnabled
        && author === 'BACKOFFICE'
        && (code === 'MODIFY_ALL_DNS' || code === 'MODIFY_CLIENT_DNS'))
    )
  );
  cdnServices$ = combineLatest([
    this._store.pipe(select(authSlice)),
    this.clientUser$,
    this._localRedirection.onChangeRedirect$
  ]).pipe(
    filter(([ authSlice, clientUser, _ ]) => !!authSlice && !!clientUser),
    map(([ authSlice, clientUser ]) => (!!clientUser.cdnEnabled
      && !!clientUser.client
      && !!clientUser.client.connectedServiceList
      && !!clientUser.client.connectedServiceList.length)
      ? clientUser.client.connectedServiceList
        .filter(
          (itm) => itm.standardService
            && itm.standardService.isActive
            && itm.standardService.code === 'CDN'
            && itm.clientZoneStatus === 'ACTIVE'
        )
        .map((itm) => {
          const path = `${authSlice.accessToken}|${authSlice.refreshToken}|${itm.id}|${clientUser.id}`;
          const encodedPath = encodeURIComponent(b64EncodeUnicode(path));
          const forceRedirectToLocalhost = this._getLocalhostRedirect();
          return {
            url: `${(forceRedirectToLocalhost ? environmentForDevel : environment).cdnAuthRedirectUrl}/${encodedPath}`,
            service: itm
          };
        })
      : []
    ),
    distinctUntilChanged()
  );
  showCloudMail$ = this.clientUser$.pipe(
    map((clientUser) => !!clientUser.cloudMailEnabled &&
      !!clientUser.client?.connectedServiceList?.find(
        (itm) => itm.standardService && itm.standardService.isActive && itm.standardService.code === 'CLOUD_MAIL'
      )
    ),
    distinctUntilChanged()
  );
  cloudMailServices$ = combineLatest([
    this._store.pipe(select(authSlice)),
    this.clientUser$,
    this._localRedirection.onChangeRedirect$
  ]).pipe(
    filter(([ authSlice, clientUser, _ ]) => !!authSlice && !!clientUser),
    map(([ authSlice, clientUser ]) => {
      if (!clientUser.cloudMailEnabled || !clientUser?.client?.connectedServiceList?.length) {
        return [];
      }

      const cloudMailAuthRedirectUrl =
        (this._getLocalhostRedirect() ? environmentForDevel : environment).cloudMailAuthRedirectUrl;

      return clientUser.client.connectedServiceList
        .filter(
          (itm) => itm.standardService
            && itm.standardService.isActive
            && itm.standardService.code === 'CLOUD_MAIL'
        )
        .map((service) => {
          const path = `${authSlice.accessToken}|${service.id}`;
          const encodedPath = encodeURIComponent(b64EncodeUnicode(path));
          return {
            id: service.id,
            url: `${cloudMailAuthRedirectUrl}/${encodedPath}`,
            service: service
          };
        });
    }),
    distinctUntilChanged()
  );
  showGraphAndSettings$ = combineLatest([
    this.graphsAndSettingsServices$,
    this.showCloudDns$,
    this.showCloudMail$,
    this.cdnServices$
  ]).pipe(
    map(([ services, showCloudDns, showCloudMail, cdnServices ]) => showCloudDns
      || (cdnServices && cdnServices.length)
      || showCloudMail
      || (services && services.length)
    )
  );
  languageId$ = this._store.pipe(
    select(identity),
    map((user) => user && user.language
      ? user.language.id
      : undefined)
  );

  @Input()
  settingsLayout: string | number;

  @Input()
  financialLayout: string | number;

  @Input()
  showSettings: boolean;

  @Input()
  showFinancial: boolean;

  // -- angular
  @Output()
  contentUpdated = new EventEmitter<void>();

  @Output()
  contentClicked = new EventEmitter<void>();

  // # Action streams
  private _contactSupportForNewServiceAction$ = this.onContactSupportForNewService$.pipe(
    mergeMap(() => [
      new SetTicketTriggerState({
        state: 1,
        meta: 'SERVICE_NEW'
      }),
      new TicketTriggerOpen()
    ])
  );

  constructor(
    private _store: Store<State>,
    private _localRedirection: LocalRedirectionService,
    private _tokenRefresh: TokenRefreshService
  ) {
    super();

    // emit that content changed so menu can resize the pop
    combineLatest([
      this.latestClientServices$,
      this.groupedServices$
    ]).pipe(takeUntil(this._ngOnDestroy$)).subscribe(
      () => this.contentUpdated.emit()
    );

    // # Store Dispatcher
    this._contactSupportForNewServiceAction$
      .pipe(takeUntil(this._ngOnDestroy$))
      .subscribe(this._store);

    this.slideControl.valueChanges
      .pipe(takeUntil(this._ngOnDestroy$))
      .subscribe((value) => {
        localStorage.setItem(localhostRedirectStorageKey, value ? '1' : '0');
        this._localRedirection.set(!!value);
      });
  }

  _trackBy(index: number) {
    return index;
  }

  openCloudMail(event: MouseEvent, serviceId: string) {
    event.preventDefault();

    /*
      Opening the tab synchronously inside the click gesture so it is not treated as a blocked popup,
      then redirect it once a fresh url is emitted
     */
    const win = window.open('', '_blank');

    this._tokenRefresh
      .ensureFreshToken$()
      .pipe(
        /* Token is now guaranteed fresh in the store, so cloudMailServices$ recomputes the url with the up to date access token */
        switchMap(() => this.cloudMailServices$.pipe(
          map((services) => services.find((service) => service.id === serviceId)),
          filter((service) => !!service),
          take(1)
        )),
        takeUntil(this._ngOnDestroy$)
      ).subscribe({
        next: (service) => {
          if (win) {
            win.location.href = service.url;
          }
        },
        error: () => {
          if (win) {
            win.close();
          }
        }
      });

    this.contentClicked.emit();
  }

  private _getLocalhostRedirect() {
    const localStorageVar = localStorage.getItem(localhostRedirectStorageKey);
    return !this.isProductionEnv
      && parseInt(localStorageVar
          ? localStorageVar
          : '0',
        10
      ) === 1;
  }
}
